import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../cn'
import { KbTooltip } from '../kb-tooltip'

const buttonVariants = cva(
  "inline-flex h-9 shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm leading-tight font-medium transition-[color,background-color,border-color,box-shadow,filter,transform,opacity] duration-150 ease-out outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:brightness-95 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        player: 'size-10 bg-transparent !p-1 text-white hover:bg-white/10',
        text: '!px-0',
      },
      size: {
        default: 'px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        menuAction: 'h-6 w-6',
        smIcon: 'size-8 p-1.5',
        md: 'h-9 px-4 py-2',
        xs: 'size-7 p-0.5',
        // Horizontal padding matches the other text sizes. Without it every
        // consumer had to hand-patch `px-*` (all 15 did), and one that forgot
        // got a hover fill flush against its label. Consumers that set their
        // own `px-*`/`p-*` still win via tailwind-merge, so this is additive.
        xxs: 'h-6 px-2.5 has-[>svg]:px-2',
        xxsIcon: 'size-6 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  tooltip?: string
  tooltipClassName?: string
  tooltipProps?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tooltip,
      tooltipClassName,
      tooltipProps,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <>
        {tooltip ? (
          <KbTooltip
            tooltip={tooltip}
            tooltipProps={tooltipProps}
            tooltipClassName={tooltipClassName}
          >
            <Comp
              className={cn(buttonVariants({ variant, size, className }))}
              ref={ref}
              {...props}
            />
          </KbTooltip>
        ) : (
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
          />
        )}
      </>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
